/**
 * Calculate rental duration periods count based on rentPeriod type
 * @param {Date|string} start
 * @param {Date|string} end
 * @param {string} rentPeriod - 'hour' | '4hours' | 'flat' | 'day' | 'week' | 'month'
 * @returns {number} number of periods (minimum 1)
 */
export function calcRentPeriods(start, end, rentPeriod = 'day') {
  const s = new Date(start)
  const e = new Date(end)
  const diffMs = Math.abs(e - s)
  const diffHours = diffMs / (1000 * 60 * 60)
  const diffDays = diffMs / (1000 * 60 * 60 * 24)

  switch (rentPeriod) {
    case 'hour':
      return Math.max(1, Math.ceil(diffHours))
    case '4hours':
      return Math.max(1, Math.ceil(diffHours / 4))
    case 'flat':
      return 1
    case 'day':
      return Math.max(1, Math.ceil(diffDays) + 1)
    case 'week':
      return Math.max(1, Math.ceil(diffDays / 7))
    case 'month':
      return Math.max(1, Math.ceil(diffDays / 30))
    default:
      return Math.max(1, Math.ceil(diffDays) + 1)
  }
}

/**
 * Calculate total rental price
 * @param {number} pricePerPeriod
 * @param {number} quantity
 * @param {Date|string} start
 * @param {Date|string} end
 * @param {string} rentPeriod
 * @returns {number}
 */
export function calcRentTotalPrice(pricePerPeriod, quantity, start, end, rentPeriod = 'day') {
  const periods = calcRentPeriods(start, end, rentPeriod)
  return pricePerPeriod * (quantity || 1) * periods
}

/**
 * Format rent duration for display
 * @param {Date|string} start
 * @param {Date|string} end
 * @param {string} rentPeriod
 * @returns {string}
 */
/**
 * Format rent date range label for display
 * @param {Date|string} start
 * @param {Date|string} end
 * @param {string} rentPeriod
 * @param {Function} formatDate - formatDate from useGlobalMixins
 * @returns {string} e.g. "11 Mar – 14 Mar" or "11 Mar, 10:00 – 14:00"
 */
export function formatRentDateLabel(start, end, rentPeriod = 'day', formatDate) {
  if (!start || !end) return ''
  const isShortPeriod = ['hour', '4hours', 'flat'].includes(rentPeriod)
  if (isShortPeriod) {
    const s = new Date(start)
    const e = new Date(end)
    const dateStr = formatDate(s, { dayMonth: true })
    const startTime = `${String(s.getHours()).padStart(2, '0')}:${String(s.getMinutes()).padStart(2, '0')}`
    const endTime = `${String(e.getHours()).padStart(2, '0')}:${String(e.getMinutes()).padStart(2, '0')}`
    return `${dateStr}, ${startTime} – ${endTime}`
  }
  return `${formatDate(start, { dayMonth: true })} – ${formatDate(end, { dayMonth: true })}`
}

export function formatRentDuration(start, end, rentPeriod = 'day') {
  const periods = calcRentPeriods(start, end, rentPeriod)

  const labels = {
    hour: { one: 'hour', many: 'hours' },
    '4hours': { one: '4 hours', many: '4 hours' },
    'flat': { one: 'flat', many: 'flat' },
    day: { one: 'day', many: 'days' },
    week: { one: 'week', many: 'weeks' },
    month: { one: 'month', many: 'months' },
  }

  const label = labels[rentPeriod] || labels.day
  return `${periods} ${periods === 1 ? label.one : label.many}`
}
